/**
 * Parses a hex color string (without the # prefix) into RGB components.
 * @param {string} hex - Hex digits (3, 6, or 8 characters)
 * @param {string} originalColor - Original color string for error messages
 * @returns {{r: number, g: number, b: number}}
 */
const parseHexColor = (hex, originalColor) => {
  if (hex.length === 3) {
    return {
      r: parseInt(hex[0] + hex[0], 16),
      g: parseInt(hex[1] + hex[1], 16),
      b: parseInt(hex[2] + hex[2], 16),
    }
  }

  if (hex.length === 6 || hex.length === 8) {
    return {
      r: parseInt(hex.slice(0, 2), 16),
      g: parseInt(hex.slice(2, 4), 16),
      b: parseInt(hex.slice(4, 6), 16),
    }
  }

  throw new Error(`Invalid hex color format: ${originalColor}`)
}

/**
 * Parses an rgb/rgba color string into RGB components.
 * @param {string} normalizedColor - Lowercase, trimmed color string
 * @param {string} originalColor - Original color string for error messages
 * @returns {{r: number, g: number, b: number}}
 */
const parseRgbColor = (normalizedColor, originalColor) => {
  const rgbMatch = normalizedColor.match(/rgba?\(([^)]+)\)/)
  if (!rgbMatch) {
    throw new Error(`Unsupported color format: ${originalColor}`)
  }

  const values = rgbMatch[1].split(",").map(v => v.trim())
  if (values.length < 3 || values.length > 4) {
    throw new Error(`Invalid rgb/rgba color format: ${originalColor}`)
  }

  const r = parseInt(values[0], 10)
  const g = parseInt(values[1], 10)
  const b = parseInt(values[2], 10)

  if (r < 0 || r > 255 || g < 0 || g > 255 || b < 0 || b > 255) {
    throw new Error(`Invalid RGB values: ${originalColor}`)
  }

  return { r, g, b }
}

/**
 * Converts a color in various formats (rgb, rgba, hex) to rgba format
 * @param {string} color - Color in rgb, rgba, or hex format
 * @param {number} opacity - Opacity value between 0 and 1
 * @returns {string} RGBA color string (rgba(r, g, b, a))
 */
export const colorToRgba = (color, opacity = 1) => {
  opacity = Math.max(0, Math.min(1, opacity))
  const normalizedColor = color.trim().toLowerCase()

  const { r, g, b } = normalizedColor.startsWith("#")
    ? parseHexColor(normalizedColor.slice(1), color)
    : parseRgbColor(normalizedColor, color)

  return `rgba(${r}, ${g}, ${b}, ${opacity})`
}
